/* eslint-disable jsx-a11y/accessible-emoji */
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {emojiMap} from '../../services/emojis/emojis';
import {fetchAsync} from '../../services/http/http';
import {httpToken} from '../../services/http/httpBase';
import {InlineEmoji} from '../common/emoji/inlineEmoji';
import {RequestInviteButton} from './requestInviteButton';

/*
 * Props.
 */

interface VisitorViewerSidebarContentRequestInviteProps {
  inviterName?: string;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: grid;
  grid-auto-rows: auto;
  grid-row-gap: 12px;
  justify-items: center;
  padding: 0 30px;
  position: relative;
`;

const StyledTitleDiv = styled.div`
  font-size: 32px;
  font-weight: ${fontWeights.bold};
  text-align: center;
`;

const StyledDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.large};
  line-height: 26px;
  text-align: center;
  max-width: 280px;
`;

const StyledSuccessTextDiv = styled.div`
  position: absolute;
  bottom: -8px;
  transform: translateY(100%);
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  max-width: 210px;
  line-height: 17px;
  text-align: center;
`;

const StyledInviterNameSpan = styled.span`
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const VisitorViewerSidebarContentRequestInvite: FC<VisitorViewerSidebarContentRequestInviteProps> = ({
  inviterName,
}) => {
  const [requestSent, setRequestSent] = useState(false);

  const requestAnInvite = () => {
    setRequestSent(true);
    fetchAsync(`/visitors/${httpToken()}/request-invite`);
  };

  const descriptionCopy = inviterName ? (
    <>
      Front makes collaborating with teammates like
      <StyledInviterNameSpan>{` ${inviterName} `}</StyledInviterNameSpan>
      easy. It transforms your inbox into the place you get work done — together.
    </>
  ) : (
    <>
      Front makes collaborating with teammates easy. It transforms your inbox into the place you get work done
      — together.
    </>
  );

  return (
    <StyledContentDiv>
      <StyledTitleDiv>
        <Trans id="visitor-sidebar-title">
          <InlineEmoji emoji={emojiMap['star-struck']} size={32} /> Like what you see?
        </Trans>
      </StyledTitleDiv>
      <StyledDescriptionDiv>
        <Trans id="visitor-sidebar-description">{descriptionCopy}</Trans>
      </StyledDescriptionDiv>
      <RequestInviteButton wasInviteRequested={requestSent} onClick={requestAnInvite} />
      {requestSent && (
        <StyledSuccessTextDiv>
          <Trans id="visitor-sidebar-request-sent">
            An admin at your company will send an invite to your inbox soon 🙌
          </Trans>
        </StyledSuccessTextDiv>
      )}
    </StyledContentDiv>
  );
};
